import styled, { css } from 'styled-components';
import { THEME } from '@/common/styles/theme';

export const StoriesBarSection = styled.section`
	z-index: 3;
	padding: 20px min(5vw, 64px) 14px;
	border-bottom: 1px solid rgba(246, 142, 255, 0.08);
	background: rgba(5, 5, 7, 0.82);
	backdrop-filter: blur(16px);
	min-width: 0;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding: 10px ${THEME.mobileGutter} 12px;
		overflow: visible;
	}
`;

export const StoriesScrollWrap = styled.div`
	position: relative;
	min-width: 0;
	width: 100%;

	&::before,
	&::after {
		content: '';
		position: absolute;
		top: 0;
		bottom: 8px;
		z-index: 1;
		width: 48px;
		pointer-events: none;
		opacity: 0;
		transition: opacity 0.16s ease;
	}

	&::before {
		left: 0;
		background: linear-gradient(90deg, rgba(5, 5, 7, 0.9), transparent);
	}

	&::after {
		right: 0;
		background: linear-gradient(270deg, rgba(5, 5, 7, 0.9), transparent);
	}

	${(props) =>
		props.$showPrevFade &&
		css`
			&::before {
				opacity: 1;
			}
		`}

	${(props) =>
		props.$showNextFade &&
		css`
			&::after {
				opacity: 1;
			}
		`}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		overflow: visible;
	}
`;

export const StoriesStrip = styled.div`
	display: flex;
	gap: 24px;
	overflow-x: auto;
	overflow-y: hidden;
	padding: 6px 36px 8px 2px;
	scrollbar-width: none;
	scroll-behavior: smooth;
	max-width: 100%;
	align-items: flex-start;

	&::-webkit-scrollbar {
		display: none;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		gap: 12px;
		padding: 10px 4px 6px 2px;
		overflow-y: visible;
		-webkit-overflow-scrolling: touch;
		scroll-padding-inline: ${THEME.mobileGutter};
	}
`;

export const StoriesScrollButton = styled.button`
	position: absolute;
	top: 41px;
	z-index: 2;
	width: 31px;
	height: 31px;
	display: grid;
	place-items: center;
	border: 1px solid rgba(255, 180, 236, 0.16);
	border-radius: 999px;
	padding: 0;
	color: rgba(255, 240, 252, 0.9);
	background:
		linear-gradient(135deg, rgba(255, 77, 242, 0.15), rgba(255, 180, 107, 0.1)),
		rgba(18, 12, 22, 0.94);
	box-shadow: 0 0 16px rgba(255, 77, 242, 0.13);
	opacity: 0;
	pointer-events: none;
	transition:
		opacity 0.16s ease,
		transform 0.16s ease,
		border-color 0.16s ease;

	${(props) =>
		props.$visible &&
		css`
			opacity: 1;
			pointer-events: auto;
		`}

	&:hover,
	&:focus-visible {
		border-color: rgba(255, 119, 244, 0.36);
		transform: translateY(-1px);
	}

	svg {
		width: 17px;
		height: 17px;
		fill: none;
		stroke: currentColor;
		stroke-width: 2.2;
		stroke-linecap: round;
		stroke-linejoin: round;
	}

	&:disabled {
		opacity: 0;
		pointer-events: none;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		display: none;
	}
`;

export const StoriesScrollPrev = styled(StoriesScrollButton)`
	left: 0;
`;

export const StoriesScrollNext = styled(StoriesScrollButton)`
	right: 0;
`;

export const StoryButton = styled.button`
	flex: 0 0 auto;
	width: 92px;
	border: 0;
	outline: 0;
	background: transparent;
	box-shadow: none;
	appearance: none;
	color: #fff9ff;
	display: grid;
	justify-items: center;
	gap: 9px;
	font-family: ${THEME.fontApp};
	font-size: 1rem;
	font-weight: 600;
	cursor: pointer;

	&[aria-hidden='true'] {
		pointer-events: none;
		cursor: default;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 78px;
		gap: 7px;
		font-size: 0.82rem;
	}
`;

export const StoryRing = styled.span`
	--story-ring-size: 82px;
	position: relative;
	width: var(--story-ring-size);
	height: var(--story-ring-size);
	flex-shrink: 0;
	border-radius: 50%;
	padding: 4px;
	display: grid;
	place-items: stretch;
	background: linear-gradient(135deg, #ff4c8b, #ff2fbf 52%, #a443ff);
	isolation: isolate;
	box-sizing: border-box;
	overflow: visible;

	&::before {
		content: '';
		position: absolute;
		inset: -9px;
		z-index: -1;
		border-radius: 50%;
		background: radial-gradient(
			circle,
			rgba(255, 47, 191, 0.42) 0%,
			rgba(164, 67, 255, 0.2) 42%,
			transparent 72%
		);
		filter: blur(7px);
		pointer-events: none;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		--story-ring-size: 68px;
		padding: 3px;

		&::before {
			inset: -5px;
			filter: blur(5px);
		}
	}

	${(props) =>
		props.$active &&
		css`
			&::before {
				background: radial-gradient(
					circle,
					rgba(255, 77, 242, 0.58) 0%,
					rgba(164, 67, 255, 0.25) 46%,
					transparent 74%
				);
			}
		`}
`;

export const StoryRingMedia = styled.span`
	position: relative;
	width: 100%;
	height: 100%;
	min-width: 0;
	min-height: 0;
	border-radius: 50%;
	overflow: hidden;
	display: block;

	> div {
		position: absolute;
		inset: 0;
		z-index: 0;
	}
`;

export const StoryRingImage = styled.img`
	width: 100%;
	height: 100%;
	border-radius: 50%;
	border: 3px solid #09070a;
	object-fit: cover;
	display: block;
	box-sizing: border-box;
	position: relative;
	z-index: 1;
	opacity: ${(props) => (props.$visible ? 1 : 0)};
	visibility: ${(props) => (props.$visible ? 'visible' : 'hidden')};
	transition: opacity 180ms ease, visibility 180ms ease;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		border-width: 2px;
	}
`;

export const StoryName = styled.span`
	max-width: 100%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	text-shadow: 0 0 12px rgba(255, 77, 242, 0.28);
	line-height: 1.15;
	padding: 0 1px;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		font-size: 0.78rem;
	}
`;
